import { CheckCircle, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "@/i18n";

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();

  const drawId = searchParams.get("lottery");
  const amount = searchParams.get("amount");
  const reference = searchParams.get("reference");
  const error = searchParams.get("error");

  return (
    <div className="mx-auto flex max-w-md min-w-[25vw] flex-col">
      {error ? (
        <ErrorCard error={error!} />
      ) : (
        <SuccessCard amount={amount!} reference={reference!} drawId={drawId!} />
      )}
    </div>
  );
};

const SuccessCard = ({
  amount,
  reference,
  drawId,
}: {
  drawId: string;
  amount: string;
  reference: string;
}) => {
  const { t } = useTranslation();

  return (
    <Card className="bg-card w-full border-emerald-100 shadow-lg">
      <CardHeader className="flex flex-col items-center space-y-2 border-b pt-8 pb-6 text-center">
        <CheckCircle className="h-16 w-16 text-emerald-500" />
        <CardTitle className="text-2xl font-bold text-emerald-700">
          {t("callback.paymentSuccessful")}
        </CardTitle>
        <CardDescription>{t("callback.transactionCompleted")}</CardDescription>
        <CardDescription>
          {drawId ? t("callback.ticketsPending") : t("callback.walletPending")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-6">
        <div className="rounded-lg bg-slate-50 p-4">
          <div className="mb-2 text-sm font-medium text-slate-500">
            {t("callback.amountPaid")}
          </div>
          <div className="text-xl font-bold text-slate-900">{amount}</div>
        </div>
        <div className="rounded-lg bg-slate-50 p-4">
          <div className="mb-2 text-sm font-medium text-slate-500">
            {t("callback.transactionReference")}
          </div>
          <div className="text-sm font-medium text-slate-900">{reference}</div>
        </div>
      </CardContent>
      <CardFooter>
        <Link to="/" className="w-full">
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
            {t("callback.returnHome")}
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
};

const ErrorCard = ({ error }: { error: string }) => {
  const { t } = useTranslation();

  return (
    <Card className="bg-card w-full border-red-100 shadow-lg">
      <CardHeader className="flex flex-col items-center space-y-2 border-b pt-8 pb-6 text-center">
        <XCircle className="h-16 w-16 text-red-500" />
        <CardTitle className="text-2xl font-bold text-red-700">
          {t("callback.paymentFailed")}
        </CardTitle>
        <CardDescription>{t("callback.paymentNotProcessed")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-6">
        <div className="rounded-lg bg-red-50 p-4">
          <div className="mb-2 text-sm font-medium text-red-500">
            {t("callback.errorDetails")}
          </div>
          <div className="text-sm text-red-900">{error}</div>
        </div>
      </CardContent>
      <CardFooter>
        <Link to="/" className="w-full">
          <Button className="w-full bg-slate-700 hover:bg-slate-800">
            {t("callback.returnHome")}
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
};

export default PaymentCallback;
